import { Component, OnInit, ChangeDetectionStrategy, Input, ViewChild, ElementRef, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  TRANSPORTATION_TRACKING_DATES_CONFIG,
  TRANSPORTATION_TRACKING_DATES_REVERSE_ORDERING,
  TransportationTrackingActionType,
  TransportationTrackingDateField,
  TransportationTrackingDateItem,
  TransportationTrackingDates,
  TransportationTrackingDateType,
} from './transportation-tracking.constant';
import { AutoUnsubscribe, DIALOG_DEFAULT_CONFIG, DialogSize, IconNames } from '../../common';
import { BehaviorSubject, fromEvent, startWith, Subscription } from 'rxjs';
import { TransportationTrackingDatesDialogComponent } from './dates-dialog/transportation-tracking-dates-dialog.component';
import { TransportOrderOverview } from '../../store/state/shipment-transport-order';
import { LoadOverview } from '../../store/state/shipment-load';
import { LocalDate } from '../../store/models';
import { DOCUMENT } from '@angular/common';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'ui-transportation-tracking',
  templateUrl: './transportation-tracking.component.html',
  styleUrls: ['./transportation-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class TransportationTrackingComponent implements OnInit {
  @Input() actionType: TransportationTrackingActionType;
  @Input() overview: TransportOrderOverview | LoadOverview;

  triangleConfig$: BehaviorSubject<{
    size: number;
    horizontalPosition: number;
  }> = new BehaviorSubject({ size: null, horizontalPosition: null });
  transportationTrackingActionType = TransportationTrackingActionType;
  transportationTrackingDateType = TransportationTrackingDateType;
  departureTime: TransportationTrackingDateItem;
  arrivalTime: TransportationTrackingDateItem;
  iconNameList = IconNames;

  private subscriptions: Subscription[] = [];

  @ViewChild('dateCard') dateCardElement: ElementRef;
  constructor(private dialog: MatDialog, @Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    if (this.actionType === this.transportationTrackingActionType.Pickup) {
      this.departureTime = this.getRelevantTrackingDate(this.overview.dates, TransportationTrackingActionType.Pickup);
    } else {
      this.arrivalTime = this.getRelevantTrackingDate(this.overview.dates, TransportationTrackingActionType.Delivery);
    }

    this.subscriptions.push(
      fromEvent(this.document.defaultView, 'resize')
        .pipe(startWith(null), debounceTime(0))
        .subscribe(() => this.setTriangleConfig()),
    );
  }

  openDatesDialog(directionType: TransportationTrackingActionType): void {
    this.dialog.open(TransportationTrackingDatesDialogComponent, {
      ...DIALOG_DEFAULT_CONFIG,
      width: DialogSize.MD,
      data: {
        actionType: directionType,
        overview: this.overview,
      },
    });
  }

  private getRelevantTrackingDate(
    allDates: TransportationTrackingDates,
    actionType: TransportationTrackingActionType,
  ): TransportationTrackingDateItem {
    const datesConfig = TRANSPORTATION_TRACKING_DATES_CONFIG[actionType];
    return TRANSPORTATION_TRACKING_DATES_REVERSE_ORDERING.map((dateType: TransportationTrackingDateType) => {
      const fieldsName: TransportationTrackingDateField[] = datesConfig[dateType];
      const values: LocalDate[] = fieldsName.map((field: TransportationTrackingDateField) => allDates[field]);
      return { type: dateType, values };
    }).filter((data: TransportationTrackingDateItem) => {
      return data.values.every((value: LocalDate) => Boolean(value));
    })[0];
  }

  private setTriangleConfig(): void {
    if (!this.dateCardElement) {
      return;
    }

    const size = this.dateCardElement.nativeElement.clientHeight / Math.sqrt(2);

    this.triangleConfig$.next({ size, horizontalPosition: size / 2 });
  }
}
